import { useEffect, useState } from 'react';
import { AlertCircle, Clock, CheckCircle, Hourglass } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { POLLING_INTERVALS, SLA_THRESHOLDS } from '@/lib/constants';

const SLATimer = ({ createdAt, breachTime, status, compact = false }) => {
  const [timeData, setTimeData] = useState(null);

  // Ticket has no SLA deadline yet (resolver hasn't set TAT)
  const tatNotSet = !breachTime;

  useEffect(() => {
    if (tatNotSet) {
      setTimeData(null);
      return;
    }
    const updateTimer = () => {
      const now = new Date();
      const created = new Date(createdAt);
      const breach = new Date(breachTime);

      const totalTime = breach - created;
      const elapsed = now - created;
      const remaining = breach - now;

      const percentageElapsed = totalTime > 0
        ? Math.min((elapsed / totalTime) * 100, 100)
        : 100;

      const hours = Math.floor(Math.abs(remaining) / (1000 * 60 * 60));
      const minutes = Math.floor((Math.abs(remaining) % (1000 * 60 * 60)) / (1000 * 60));

      setTimeData({
        percentageElapsed,
        remaining,
        hours,
        minutes,
        isBreached: remaining < 0
      });
    };

    updateTimer();
    const interval = setInterval(updateTimer, POLLING_INTERVALS.SLA_TIMER);
    return () => clearInterval(interval);
  }, [createdAt, breachTime, tatNotSet]);

  if (status === 'Resolved') {
    return compact ? (
      <div className="flex items-center gap-1 text-green-600 text-xs" data-testid="sla-timer-resolved-compact">
        <CheckCircle className="w-3 h-3" />
        <span>Resolved</span>
      </div>
    ) : (
      <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center" data-testid="sla-timer-resolved">
        <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-2" />
        <p className="text-sm font-semibold text-green-800">Ticket Resolved</p>
      </div>
    );
  }

  // No TAT set yet → no SLA deadline, show awaiting state (never BREACHED)
  if (tatNotSet) {
    if (compact) {
      return (
        <div className="flex items-center gap-1 text-xs" data-testid="sla-timer-awaiting-tat-compact">
          <Hourglass className="w-3 h-3 text-slate-500" />
          <span className="text-slate-600">Awaiting TAT</span>
        </div>
      );
    }
    return (
      <div className="space-y-2" data-testid="sla-timer-awaiting-tat">
        <div className="flex items-center justify-between text-sm">
          <span className="text-slate-600">SLA Status</span>
          <span className="flex items-center gap-1 text-slate-600 font-medium">
            <Hourglass className="w-4 h-4" />
            Awaiting Resolver TAT
          </span>
        </div>
        <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-center">
          <p className="text-xs text-slate-600">
            SLA tracking will start once the resolver submits a Turn-Around-Time (TAT) to resolve this ticket.
          </p>
        </div>
      </div>
    );
  }

  if (!timeData) return null;

  const getProgressColor = () => {
    if (timeData.isBreached) return 'bg-red-500';
    if (timeData.percentageElapsed > SLA_THRESHOLDS.WARNING) return 'bg-red-500';
    if (timeData.percentageElapsed > SLA_THRESHOLDS.DANGER) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  if (compact) {
    return (
      <div className="flex items-center gap-1 text-xs">
        {timeData.isBreached ? (
          <>
            <AlertCircle className="w-3 h-3 text-red-600 animate-pulse" />
            <span className="text-red-600 font-semibold">BREACHED</span>
          </>
        ) : (
          <>
            <Clock className="w-3 h-3 text-slate-600" />
            <span className="font-mono text-slate-700">{timeData.hours}h {timeData.minutes}m</span>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between text-sm">
        <span className="text-slate-600">SLA Status</span>
        {timeData.isBreached ? (
          <span className="flex items-center gap-1 text-red-600 font-semibold animate-pulse">
            <AlertCircle className="w-4 h-4" />
            BREACHED
          </span>
        ) : (
          <span className="flex items-center gap-1 text-slate-700 font-mono">
            <Clock className="w-4 h-4" />
            {timeData.hours}h {timeData.minutes}m left
          </span>
        )}
      </div>

      <div className="space-y-2">
        <Progress
          value={timeData.percentageElapsed}
          className="h-2"
          indicatorClassName={getProgressColor()}
        />
        <div className="flex justify-between text-xs text-slate-500">
          <span>Deadline</span>
          <span>{new Date(breachTime).toLocaleString()}</span>
        </div>
      </div>

      {timeData.isBreached && (
        <div className="bg-red-50 border border-red-200 rounded-md p-2 text-center">
          <p className="text-xs text-red-700 font-medium">
            Overdue by {timeData.hours}h {timeData.minutes}m
          </p>
        </div>
      )}
    </div>
  );
};

export default SLATimer;
